import React from "react";

// Source
import StoreRole from "../../store/Role";
import StoreRoute from "../../store/Route";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import * as HelperSrc from "../../HelperSrc";
import * as ModelRole from "../../model/Role";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ApiPayee from "../../api/custom/Payee";
import * as ModelPayee from "../../model/custom/Payee";
import ControllerAlert from "../Alert";

export default class ControllerPayeeProfile {
    // Variable
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementBottom = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private validationForm = React.useRef<ModelPayee.Idata | undefined>(undefined);
    private dataMain = React.useRef<ModelPayee.Idata | undefined>(undefined);
    private language = React.useRef<string>("");

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelPayee.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelPayee.IselectOption | undefined>>;

    getDataMain: ModelPayee.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelPayee.Idata | undefined>>;

    getValidationForm: ModelPayee.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelPayee.Idata | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementClientIdValue: string;
    setElementClientIdValue: React.Dispatch<React.SetStateAction<string>>;

    getElementAccountTypeIdValue: string;
    setElementAccountTypeIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelPayee.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getDataMain, setDataMain] = React.useState<ModelPayee.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getValidationForm, setValidationForm] = React.useState<ModelPayee.Idata | undefined>(this.validationForm.current);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementClientIdValue, setElementClientIdValue] = React.useState<string>("");
        this.getElementClientIdValue = getElementClientIdValue;
        this.setElementClientIdValue = setElementClientIdValue;

        const [getElementAccountTypeIdValue, setElementAccountTypeIdValue] = React.useState<string>("");
        this.getElementAccountTypeIdValue = getElementAccountTypeIdValue;
        this.setElementAccountTypeIdValue = setElementAccountTypeIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_payee_profile", () => {
                this.initializeHtmlElement();
            });

            const historyState = window.history.state as Record<string, unknown> | undefined;

            if (historyState) {
                const parameterList = historyState.parameterList as ModelPayee.Idata;

                if (parameterList) {
                    this.dataMain.current = { ...parameterList };
                } else {
                    this.dataMain.current = {} as ModelPayee.Idata;
                }
            } else {
                this.dataMain.current = {} as ModelPayee.Idata;
            }

            this.setDataMain(this.dataMain.current);

            this.apiValidationForm();

            this.apiSelectOption();

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm();
            });
        }, []);

        React.useEffect(() => {
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageRoleData.current, this.storageRouteData.current, this.storageLocalizationData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    clickChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        this.setApiLoadingList((prev) => ({
            ...prev,
            profileChange: true
        }));

        if (this.dataMain.current && this.dataMain.current.id) {
            const id = this.dataMain.current.id;
            const client_id = this.readHtmlElementValue("client_id") as unknown as number;
            const bank_name = this.readHtmlElementValue("bank_name");
            const branch_name = this.readHtmlElementValue("branch_name");
            const bank_code = this.readHtmlElementValue("bank_code");
            const branch_code = this.readHtmlElementValue("branch_code");
            const account_type_id = this.readHtmlElementValue("account_type_id") as unknown as number;
            const ac_number = this.readHtmlElementValue("ac_number");
            const account_name = this.readHtmlElementValue("account_name");
            const account_name_kanji = this.readHtmlElementValue("account_name_kanji");
            const swift = this.readHtmlElementValue("swift");
            const url = this.readHtmlElementValue("url");
            const is_active = this.readHtmlElementCheckbox("is_active");

            ApiPayee.updateData(
                id,
                client_id,
                bank_name,
                branch_name,
                bank_code,
                branch_code,
                account_type_id,
                ac_number,
                account_name,
                account_name_kanji,
                swift,
                url,
                is_active,
                (response) => {
                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        profileChange: false
                    }));

                    if (response instanceof Error || response.message.error) {
                        if (response instanceof Error) {
                            HelperSrc.writeLog("error", response.message);
                        } else {
                            this.controllerAlert.show("error", response.message.error, false);
                        }

                        return;
                    }

                    this.controllerAlert.show("success", response.message.success, true);
                }
            );
        } else {
            ApiPayee.createData(
                this.readHtmlElementValue("client_id") as unknown as number,
                this.readHtmlElementValue("bank_name"),
                this.readHtmlElementValue("branch_name"),
                this.readHtmlElementValue("bank_code"),
                this.readHtmlElementValue("branch_code"),
                this.readHtmlElementValue("account_type_id") as unknown as number,
                this.readHtmlElementValue("ac_number"),
                this.readHtmlElementValue("account_name"),
                this.readHtmlElementValue("account_name_kanji"),
                this.readHtmlElementValue("swift"),
                this.readHtmlElementValue("url"),
                this.readHtmlElementCheckbox("is_active"),
                (response) => {
                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        profileChange: false
                    }));

                    if (response instanceof Error || response.message.error) {
                        if (response instanceof Error) {
                            HelperSrc.writeLog("error", response.message);
                        } else {
                            this.controllerAlert.show("error", response.message.error, false);
                        }

                        return;
                    }

                    this.controllerAlert.show("success", response.message.success, true);
                }
            );
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_payee_profile");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiPayee.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelPayee.IselectOption);
        });
    };

    private readHtmlElementValue = (tag: string): string => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.value : "";
    };

    private readHtmlElementCheckbox = (tag: string): boolean => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.checked : false;
    };

    private apiValidationForm = (): void => {
        ApiPayee.validationForm((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelPayee.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };
}
